/**
 * Customer Gold Allocation popup.
 *
 * Opened from a customer's NAME on the Customers page. Answers the operator
 * question: "In a given period, how much gold was GIVEN to each goldsmith for
 * THIS customer's order forms?" — counting every INFLOW movement recorded on
 * the customer's orders:
 *   • fresh issue (gold handed directly to the order),
 *   • attribution (gold moved from the goldsmith's Unattributed pool), and
 *   • transfer-in (gold transferred from any other order of the same goldsmith).
 *
 * A From / To date range narrows the window and a Gold 999 / Gold 995 toggle
 * rescales every gram figure (grams_995 = grams_999 / 0.995), mirroring the
 * Goldsmith / Customer gold ledgers.
 *
 * Clicking a goldsmith opens a second dialog that lists the goldsmith's order
 * forms grouped by page number. Order numbers link to the order detail page;
 * page numbers jump to the Orders list filtered on that page.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DatePicker } from '@/components/ui/date-picker';
import { Skeleton } from '@/components/ui/skeleton';
import { formatGrams, formatDate } from '@/lib/format';
import { extractError } from '@/lib/errors';
import {
  Coins, X, Loader2, Hammer, ChevronRight, FileText, Hash, ArrowUpRight, Layers,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Gold purity view (999 ↔ 995) — identical convention to the gold ledgers.
// ---------------------------------------------------------------------------
const GOLD_PURITY_KEY = 'customers.goldAllocation.purityView';

function readGoldPurity() {
  try {
    return localStorage.getItem(GOLD_PURITY_KEY) === '995' ? '995' : '999';
  } catch {
    return '999';
  }
}

function GoldPurityToggle({ purity, onChange, testId = 'cga-purity-toggle' }) {
  const opts = [
    { v: '999', label: 'Gold 999', hint: '100%' },
    { v: '995', label: 'Gold 995', hint: '99.5%' },
  ];
  return (
    <div
      className="inline-flex items-center rounded-md border border-amber-300 overflow-hidden text-[11px] shadow-sm"
      role="group"
      aria-label="Gold purity view"
      data-testid={testId}
    >
      {opts.map((o) => {
        const active = purity === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            aria-pressed={active}
            className={`px-3 py-1.5 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:relative ${
              active ? 'bg-amber-500 text-white' : 'bg-white text-slate-600 hover:bg-amber-50'
            }`}
            data-testid={`${testId}-${o.v}`}
          >
            {o.label} <span className="opacity-70">({o.hint})</span>
          </button>
        );
      })}
    </div>
  );
}

// Small labelled chip for the per-source breakdown on each goldsmith row.
function SourceChip({ label, grams, unit, tone, testId }) {
  if (!grams) return null;
  const tones = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    violet: 'bg-violet-50 text-violet-700 border-violet-200',
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${tones[tone] || tones.blue}`}
      data-testid={testId}
    >
      {label}
      <span className="font-mono tabular-nums">{formatGrams(grams)} g{unit}</span>
    </span>
  );
}

export function CustomerGoldAllocationDialog({ open, onOpenChange, customer, companyId }) {
  const { effectiveCompanyId } = useAuth();
  const cid = companyId || effectiveCompanyId;
  const navigate = useNavigate();

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [purity, setPurity] = useState(readGoldPurity());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Selected goldsmith object → drives the drill-down dialog.
  const [selected, setSelected] = useState(null);

  const customerId = customer?.id;
  const unit = data?.purity_basis || purity;
  const refLabel = data?.reference_label || 'Page No.';

  const changePurity = (v) => {
    const next = v === '995' ? '995' : '999';
    setPurity(next);
    try { localStorage.setItem(GOLD_PURITY_KEY, next); } catch { /* ignore */ }
  };

  const load = async () => {
    if (!open || !customerId) return;
    setLoading(true);
    try {
      const { data: res } = await api.get(`/customers/${customerId}/gold-allocation`, {
        params: {
          company_id: cid,
          date_from: from || undefined,
          date_to: to || undefined,
          purity,
        },
      });
      setData(res);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load gold allocation'));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { setSelected(null); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId, from, to, purity]);

  const goldsmiths = data?.goldsmiths || [];
  const grand = data?.grand_total_grams || 0;

  const periodCaption = (from || to)
    ? `${from ? formatDate(`${from}T00:00:00`) : 'Beginning'} – ${to ? formatDate(`${to}T00:00:00`) : 'Today'}`
    : 'All time';

  // Navigation helpers (close everything first, then route).
  const goToOrder = (orderId) => {
    onOpenChange(false);
    navigate(`/orders/${orderId}`);
  };
  const goToPage = (referenceNo) => {
    if (referenceNo == null || referenceNo === '') return;
    onOpenChange(false);
    navigate(`/orders?reference_no=${encodeURIComponent(referenceNo)}`);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="customer-gold-allocation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins size={18} className="text-amber-600" />
              Gold Issued for {customer?.name || 'Customer'}&rsquo;s Orders
            </DialogTitle>
            <DialogDescription>
              Total gold given to each goldsmith for this customer&rsquo;s order forms in the selected
              period — fresh issues, attributions from the goldsmith&rsquo;s unattributed pool, and
              material transferred in from other orders of the same goldsmith.
            </DialogDescription>
          </DialogHeader>

          {/* Controls: date range + purity toggle */}
          <div className="flex flex-wrap items-end justify-between gap-2 border-b border-[var(--divider)] pb-3">
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wide text-slate-500">From</label>
                <DatePicker value={from} onValueChange={setFrom} placeholder="DD-MM-YYYY" className="h-9 w-[150px]" maxDate={to || undefined} data-testid="cga-from" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wide text-slate-500">To</label>
                <DatePicker value={to} onValueChange={setTo} placeholder="DD-MM-YYYY" className="h-9 w-[150px]" minDate={from || undefined} data-testid="cga-to" />
              </div>
              {(from || to) && (
                <Button variant="ghost" size="sm" className="h-9 text-xs text-slate-500" onClick={() => { setFrom(''); setTo(''); }} data-testid="cga-clear-dates">
                  <X size={13} className="mr-1" /> Clear
                </Button>
              )}
            </div>
            <GoldPurityToggle purity={purity} onChange={changePurity} testId="cga-purity-toggle" />
          </div>

          {/* Period summary */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1" data-testid="cga-period-caption">
              {periodCaption}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-200 bg-amber-50 text-amber-900 text-[11px]">
              <Coins size={12} />
              <span className="text-[10px] uppercase tracking-wide">Total gold issued</span>
              <span className="font-semibold font-mono tabular-nums" data-testid="cga-grand-total">{formatGrams(grand)} g{unit}</span>
            </span>
            {!loading && (
              <span className="text-[10px] text-slate-400" data-testid="cga-goldsmith-count">
                {goldsmiths.length} goldsmith{goldsmiths.length === 1 ? '' : 's'}
              </span>
            )}
          </div>

          {/* Goldsmith list */}
          <div className="space-y-2" data-testid="cga-goldsmith-list">
            {loading ? (
              <>
                <Skeleton className="h-16 w-full rounded-lg" />
                <Skeleton className="h-16 w-full rounded-lg" />
              </>
            ) : goldsmiths.length === 0 ? (
              <div className="py-14 text-center text-sm text-slate-500" data-testid="cga-empty">
                No gold was issued to any goldsmith for this customer&rsquo;s orders
                {(from || to) ? ' in the selected period.' : ' yet.'}
              </div>
            ) : (
              goldsmiths.map((g) => (
                <button
                  key={g.goldsmith_id || g.goldsmith_name}
                  type="button"
                  onClick={() => setSelected(g)}
                  className="w-full text-left rounded-lg border border-[var(--divider)] bg-white px-4 py-3 hover:border-amber-300 hover:bg-amber-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 transition-colors group"
                  data-testid={`cga-goldsmith-row-${g.goldsmith_id || 'unknown'}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                        <Hammer size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-[var(--ink-navy)] truncate" data-testid={`cga-goldsmith-name-${g.goldsmith_id || 'unknown'}`}>
                          {g.goldsmith_name}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {g.order_count} order form{g.order_count === 1 ? '' : 's'} · {g.entry_count} movement{g.entry_count === 1 ? '' : 's'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <div className="text-lg font-semibold text-amber-700 font-mono tabular-nums leading-none" data-testid={`cga-goldsmith-total-${g.goldsmith_id || 'unknown'}`}>
                          {formatGrams(g.total_grams)}
                        </div>
                        <div className="text-[10px] text-slate-400 uppercase tracking-wide">g{unit}</div>
                      </div>
                      <ChevronRight size={18} className="text-slate-300 group-hover:text-amber-500 transition-colors" />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2 pl-12">
                    <SourceChip label="Fresh issue" grams={g.by_source?.fresh_issue} unit={unit} tone="blue" testId={`cga-src-issue-${g.goldsmith_id || 'unknown'}`} />
                    <SourceChip label="From pool" grams={g.by_source?.attribution} unit={unit} tone="violet" testId={`cga-src-attr-${g.goldsmith_id || 'unknown'}`} />
                    <SourceChip label="Transferred in" grams={g.by_source?.transfer_in} unit={unit} tone="teal" testId={`cga-src-transfer-${g.goldsmith_id || 'unknown'}`} />
                  </div>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <GoldsmithDrilldownDialog
        goldsmith={selected}
        onClose={() => setSelected(null)}
        customer={customer}
        unit={unit}
        refLabel={refLabel}
        onOrderClick={goToOrder}
        onPageClick={goToPage}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Drill-down: a single goldsmith's order forms grouped by page number.
// ---------------------------------------------------------------------------
function GoldsmithDrilldownDialog({ goldsmith, onClose, customer, unit, refLabel, onOrderClick, onPageClick }) {
  const open = !!goldsmith;
  const pages = goldsmith?.pages || [];
  const refLabelClean = (refLabel || 'Page No.').replace(/\.$/, '');

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="cga-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hammer size={17} className="text-amber-600" />
            {goldsmith?.goldsmith_name || 'Goldsmith'}
          </DialogTitle>
          <DialogDescription>
            Order forms of <span className="font-medium">{customer?.name || 'this customer'}</span> for which gold
            was issued to this goldsmith, grouped by {refLabelClean.toLowerCase()}. Tap an order form to open it, or tap
            the {refLabelClean.toLowerCase()} heading to view all its orders.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 pb-2 border-b border-[var(--divider)]">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-amber-200 bg-amber-50 text-amber-900 text-[11px]">
            <Coins size={12} />
            <span className="text-[10px] uppercase tracking-wide">Total</span>
            <span className="font-semibold font-mono tabular-nums" data-testid="cga-drilldown-total">{formatGrams(goldsmith?.total_grams || 0)} g{unit}</span>
          </span>
        </div>

        <div className="space-y-3" data-testid="cga-drilldown-pages">
          {pages.map((p, idx) => {
            const hasRef = p.reference_no != null && p.reference_no !== '';
            return (
              <div key={hasRef ? String(p.reference_no) : `none-${idx}`} className="rounded-lg border border-[var(--divider)] overflow-hidden">
                {/* Page header */}
                <div className="flex items-center justify-between gap-2 bg-slate-50 px-3 py-2">
                  {hasRef ? (
                    <button
                      type="button"
                      onClick={() => onPageClick(p.reference_no)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-700 hover:text-indigo-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 rounded"
                      title={`View all orders on ${refLabelClean} ${p.reference_no}`}
                      data-testid={`cga-page-link-${p.reference_no}`}
                    >
                      <Hash size={13} />
                      {refLabelClean} {p.reference_no}
                      <ArrowUpRight size={12} className="opacity-60" />
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500" data-testid="cga-page-none">
                      <Layers size={13} />
                      No {refLabelClean}
                    </span>
                  )}
                  <span className="text-xs font-mono tabular-nums font-semibold text-amber-700" data-testid={`cga-page-total-${hasRef ? p.reference_no : 'none'}`}>
                    {formatGrams(p.total_grams)} g{unit}
                  </span>
                </div>
                {/* Orders under this page */}
                <div className="divide-y divide-[var(--divider)]">
                  {p.orders.map((o) => (
                    <button
                      key={o.order_id}
                      type="button"
                      onClick={() => onOrderClick(o.order_id)}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-indigo-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 transition-colors group"
                      data-testid={`cga-order-link-${o.order_id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText size={14} className="text-slate-400 flex-shrink-0 group-hover:text-indigo-500 transition-colors" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-indigo-700 group-hover:underline truncate">
                            {o.order_no}
                          </div>
                          {o.title && <div className="text-[11px] text-slate-500 truncate max-w-[320px]">{o.title}</div>}
                        </div>
                      </div>
                      <span className="text-xs font-mono tabular-nums text-slate-700 flex-shrink-0" data-testid={`cga-order-grams-${o.order_id}`}>
                        {formatGrams(o.grams)} g{unit}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {pages.length === 0 && (
            <div className="py-10 text-center text-sm text-slate-500" data-testid="cga-drilldown-empty">
              No order forms to show.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CustomerGoldAllocationDialog;
